import { useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef } from "react";

import { ROW_BATCH_LIMIT } from "../constants";
import { getFieldValue } from "./format";
import { combineFilters, joinWith } from "./filter";
import { compileFilter, sortItems } from "../store/local-query";

export const pathKey = path => JSON.stringify(path);

function strictKeyEqual(a, b) {
  if (a == null || b == null) return a == null && b == null;

  return a === b || String(a) === String(b);
}

function looseKey(value) {
  if (value == null) return "\u0000null";
  if (value instanceof Date) return `d${value.getTime()}`;
  if (typeof value === "string") return `s${value.toLowerCase()}`;

  return `${typeof value}${String(value)}`;
}

function createNode(path, level, key, count) {
  return { path, level, key, count, children: null, total: null };
}

function keyCondition(group, key) {
  return [group.selector, "=", key];
}

function pathFilter(groups, path) {
  return joinWith(
    path.map((key, index) => keyCondition(groups[index], key)),
    "and"
  );
}

function isExpandedIn(expansion, path) {
  const override = expansion.overrides.get(pathKey(path));

  return override === undefined ? expansion.defaultExpanded : override;
}

export function flattenTree(root, groupCount, expansion, keyOf) {
  const rows = [];
  let frontier = null;

  const visit = node => {
    const leafParent = node.level === groupCount - 1;

    (node.children || []).forEach(child => {
      if (frontier) return;

      if (leafParent) {
        rows.push({ rowType: "data", data: child, key: keyOf(child), groupIndex: groupCount });
        return;
      }

      const expanded = isExpandedIn(expansion, child.path);

      rows.push({
        rowType: "group",
        key: child.path,
        data: { key: child.key, items: child.children, count: child.count },
        groupIndex: child.level,
        isExpanded: expanded,
        values: child.path,
        node: child,
      });

      if (expanded) visit(child);
    });

    if (
      !frontier &&
      (node.children === null || node.total == null || node.children.length < node.total)
    )
      frontier = node;
  };

  visit(root);

  return { rows, frontier };
}

function findSiblings(root, node) {
  let parent = root;

  for (let level = 0; level < node.level; level += 1) {
    const key = node.path[level];

    parent = (parent.children || []).find(child => strictKeyEqual(child.key, key));
    if (!parent) return [node];
  }

  return parent.children || [node];
}

function treeItems(node, groupCount) {
  if (node.level === groupCount - 1) return node.children || [];

  return (node.children || []).map(child => ({
    key: child.key,
    count: child.count,
    items: treeItems(child, groupCount),
  }));
}

class TreeLoader {
  constructor({ load, filter, sort, groups, pageSize, keyOf, initialTake }) {
    this.load = load;
    this.filter = filter;
    this.sort = sort;
    this.groups = groups;
    this.pageSize = pageSize;
    this.keyOf = keyOf;
    this.initialTake = initialTake;
    this.root = createNode([], -1, undefined, undefined);
    this.groupCount = -1;
  }

  async loadGroups(node, signal) {
    const { groups, pageSize } = this;
    const loaded = node.children ? node.children.length : 0;
    const level = node.level + 1;
    const isRootStart = node.level === -1 && loaded === 0;
    const result = await this.load(
      {
        skip: loaded,
        take: pageSize,
        group: [
          { selector: groups[level].selector, desc: !!groups[level].desc, isExpanded: false },
        ],
        requireGroupCount: true,
        filter: combineFilters(this.filter, pathFilter(groups, node.path)),
      },
      { signal }
    );

    if (signal.aborted) return;

    const children = result.data.map(item =>
      createNode([...node.path, item.key], level, item.key, item.count)
    );

    if (isRootStart) this.groupCount = result.groupCount;

    node.children = [...(node.children || []), ...children];

    if (children.length === 0) node.total = node.children.length;
    else if (result.groupCount >= 0) node.total = result.groupCount;
    else node.total = children.length < pageSize ? node.children.length : node.children.length + 1;
  }

  async loadRootRows(node, signal) {
    const { pageSize } = this;
    const loaded = node.children ? node.children.length : 0;
    const take = loaded === 0 ? Math.max(pageSize, this.initialTake || 0) : pageSize;
    const result = await this.load(
      {
        skip: loaded,
        take,
        filter: this.filter,
        sort: this.sort,
      },
      { signal }
    );

    if (signal.aborted) return;

    node.children = [...(node.children || []), ...result.data];
    node.total = result.data.length < take ? node.children.length : node.children.length + 1;
  }

  async loadGroupRows(node, expansion, signal) {
    const { groups, pageSize } = this;
    const sort = [
      ...groups.map(({ selector, desc }) => ({ selector, desc: !!desc })),
      ...this.sort,
    ];

    if (node.children !== null || (node.count || 0) > ROW_BATCH_LIMIT) {
      const loaded = node.children ? node.children.length : 0;
      const result = await this.load(
        {
          skip: loaded,
          take: pageSize,
          filter: combineFilters(this.filter, pathFilter(groups, node.path)),
          sort,
        },
        { signal }
      );

      if (signal.aborted) return;

      node.children = [...(node.children || []), ...result.data];
      node.total = result.data.length === 0 ? node.children.length : node.count;

      return;
    }

    const siblings = findSiblings(this.root, node);
    const batch = [node];
    let accumulated = node.count || 0;

    for (let index = siblings.indexOf(node) + 1; index < siblings.length; index += 1) {
      const sibling = siblings[index];

      if (sibling.children !== null || !isExpandedIn(expansion, sibling.path)) break;
      if (accumulated + (sibling.count || 0) > ROW_BATCH_LIMIT) break;

      batch.push(sibling);
      accumulated += sibling.count || 0;
    }

    const leafGroup = groups[node.level];
    const result = await this.load(
      {
        skip: 0,
        take: Math.max(accumulated, 1),
        filter: combineFilters(
          this.filter,
          pathFilter(groups, node.path.slice(0, -1)),
          joinWith(
            batch.map(group => keyCondition(leafGroup, group.key)),
            "or"
          )
        ),
        sort,
      },
      { signal }
    );

    if (signal.aborted) return;

    const children = batch.map(() => []);

    result.data.forEach(row => {
      const value = getFieldValue(row, leafGroup.selector);
      const index = batch.findIndex(group => strictKeyEqual(group.key, value));

      if (index >= 0) children[index].push(row);
    });

    batch.forEach((group, index) => {
      group.children = children[index];
      group.total = children[index].length;
    });
  }

  loadNode(node, expansion, signal) {
    if (node.level < this.groups.length - 1) return this.loadGroups(node, signal);
    if (node.level === -1) return this.loadRootRows(node, signal);

    return this.loadGroupRows(node, expansion, signal);
  }

  flatten(expansion) {
    return flattenTree(this.root, this.groups.length, expansion, this.keyOf);
  }
}

function buildLocalTree(items, groups, getGetter) {
  const root = createNode([], -1, undefined, items.length);

  if (groups.length === 0) {
    root.children = items;
    root.total = items.length;

    return root;
  }

  const build = (node, list) => {
    const level = node.level + 1;
    const getter = getGetter(groups[level].selector);
    const buckets = new Map();

    list.forEach(item => {
      const value = getter(item);
      const bucketKey = looseKey(value);
      let bucket = buckets.get(bucketKey);

      if (!bucket) {
        bucket = { key: value, items: [] };
        buckets.set(bucketKey, bucket);
      }

      bucket.items.push(item);
    });

    node.children = Array.from(buckets.values()).map(bucket => {
      const child = createNode([...node.path, bucket.key], level, bucket.key, bucket.items.length);

      if (level === groups.length - 1) {
        child.children = bucket.items;
        child.total = bucket.items.length;
      } else build(child, bucket.items);

      return child;
    });
    node.total = node.children.length;
  };

  build(root, items);

  return root;
}

const EMPTY_RESULT = {
  root: null,
  rows: [],
  allRows: [],
  totalCount: -1,
  groupCount: -1,
  hasMore: false,
  pageCount: 1,
};

function statusReducer(state, action) {
  switch (action.type) {
    case "start":
      return { ...state, phase: action.phase, error: null };
    case "done":
      return { phase: "idle", error: null, version: state.version + 1 };
    case "error":
      return { ...state, phase: "idle", error: action.error };
    case "touch":
      return { ...state, version: state.version + 1 };
    default:
      return state;
  }
}

export function useGridData(params) {
  const {
    source,
    remote,
    filter,
    sort,
    groups,
    mode,
    pageSize,
    pageIndex,
    autoExpandAll,
    refreshToken,
  } = params;
  const [status, dispatch] = useReducer(statusReducer, {
    phase: "initial",
    error: null,
    version: 0,
  });
  const paramsRef = useRef(params);
  const resultRef = useRef(EMPTY_RESULT);
  const loaderRef = useRef(null);
  const abortRef = useRef(null);
  const loadingRef = useRef(false);
  const localItemsRef = useRef(null);
  const localVisibleRef = useRef(pageSize);
  const lastRefreshRef = useRef(refreshToken);
  const lastQueryRef = useRef(null);
  const expansionKeyRef = useRef(null);
  const expansionRef = useRef({ defaultExpanded: autoExpandAll !== false, overrides: new Map() });

  paramsRef.current = params;

  const isLocal = source.kind === "local" || !remote;
  const grouped = groups.length > 0;
  const paged = mode === "standard" && !grouped;

  const queryKey = useMemo(
    () =>
      JSON.stringify({
        filter,
        sort: sort.map(({ selector, desc }) => [
          typeof selector === "string" ? selector : "fn",
          desc,
        ]),
        groups: groups.map(({ selector, desc }) => [selector, desc]),
        mode,
        pageSize,
      }),
    [filter, sort, groups, mode, pageSize]
  );

  const keyOf = useCallback(data => {
    const keyExpr = paramsRef.current.source.key;

    return keyExpr ? getFieldValue(data, keyExpr) : data;
  }, []);

  const abort = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = null;
  }, []);

  const runLoad = useCallback(
    async (task, phase) => {
      abort();

      const controller = new AbortController();

      abortRef.current = controller;
      loadingRef.current = true;
      dispatch({ type: "start", phase });

      try {
        await task(controller.signal);

        if (controller.signal.aborted) return;

        loadingRef.current = false;
        dispatch({ type: "done" });
      } catch (error) {
        if (controller.signal.aborted || error?.name === "AbortError") return;

        loadingRef.current = false;
        dispatch({ type: "error", error });
      }
    },
    [abort]
  );

  const computeLocal = useCallback(() => {
    const { current } = paramsRef;
    const items =
      current.source.kind === "local"
        ? current.source.getItems() || []
        : localItemsRef.current || [];
    const getter = current.getGetter;
    const filtered = current.filter ? items.filter(compileFilter(current.filter, getter)) : items;
    const sorted = sortItems(
      filtered,
      [...current.groups.map(({ selector, desc }) => ({ selector, desc })), ...current.sort],
      getter
    );
    const root = buildLocalTree(sorted, current.groups, getter);
    const { rows: allRows } = flattenTree(root, current.groups.length, expansionRef.current, keyOf);
    const currentPaged = current.mode === "standard" && current.groups.length === 0;
    let rows;
    let hasMore = false;

    if (currentPaged) {
      rows = allRows.slice(
        current.pageIndex * current.pageSize,
        (current.pageIndex + 1) * current.pageSize
      );
    } else {
      rows = allRows.slice(0, Math.max(localVisibleRef.current, current.pageSize));
      hasMore = rows.length < allRows.length;
    }

    resultRef.current = {
      root,
      rows,
      allRows,
      totalCount: filtered.length,
      groupCount: current.groups.length ? root.children.length : -1,
      hasMore,
      pageCount: currentPaged ? Math.max(1, Math.ceil(allRows.length / current.pageSize)) : 1,
    };
  }, [keyOf]);

  const commitRemote = useCallback(() => {
    const loader = loaderRef.current;

    if (!loader) return;

    const { rows, frontier } = loader.flatten(expansionRef.current);

    resultRef.current = {
      root: loader.root,
      rows,
      allRows: rows,
      totalCount: -1,
      groupCount: loader.groupCount,
      hasMore: !!frontier,
      pageCount: 1,
    };
  }, []);

  const reloadRemote = useCallback(
    (preserveCount, phase) => {
      const { current } = paramsRef;
      const currentPaged = current.mode === "standard" && current.groups.length === 0;

      return runLoad(async signal => {
        if (currentPaged) {
          const result = await current.source.load(
            {
              skip: current.pageIndex * current.pageSize,
              take: current.pageSize,
              requireTotalCount: true,
              filter: current.filter,
              sort: current.sort,
            },
            { signal }
          );

          if (signal.aborted) return;

          loaderRef.current = null;
          resultRef.current = {
            root: { level: -1, children: result.data },
            rows: result.data.map(data => ({
              rowType: "data",
              data,
              key: keyOf(data),
              groupIndex: 0,
            })),
            allRows: [],
            totalCount: result.totalCount,
            groupCount: -1,
            hasMore: false,
            pageCount: Math.max(1, Math.ceil(Math.max(result.totalCount, 0) / current.pageSize)),
          };

          return;
        }

        const target = Math.max(preserveCount || 0, current.pageSize);
        const loader = new TreeLoader({
          load: current.source.load,
          filter: current.filter,
          sort: current.sort,
          groups: current.groups,
          pageSize: current.pageSize,
          keyOf,
          initialTake: current.groups.length === 0 ? preserveCount : 0,
        });

        const fill = async () => {
          const { rows, frontier } = loader.flatten(expansionRef.current);

          if (signal.aborted || !frontier || rows.length >= target) return;

          await loader.loadNode(frontier, expansionRef.current, signal);
          await fill();
        };

        await fill();

        if (signal.aborted) return;

        loaderRef.current = loader;
        commitRemote();
      }, phase);
    },
    [commitRemote, keyOf, runLoad]
  );

  const loadAllRemote = useCallback(
    phase =>
      runLoad(async signal => {
        const result = await paramsRef.current.source.load({}, { signal });

        if (signal.aborted) return;

        localItemsRef.current = result.data;
        computeLocal();
      }, phase),
    [computeLocal, runLoad]
  );

  const expansionKey = useMemo(
    () => JSON.stringify([groups.map(({ selector }) => selector), autoExpandAll !== false]),
    [groups, autoExpandAll]
  );

  useLayoutEffect(() => {
    const refreshed = lastRefreshRef.current !== refreshToken;

    lastRefreshRef.current = refreshToken;

    if (expansionKeyRef.current !== expansionKey) {
      expansionKeyRef.current = expansionKey;
      expansionRef.current = { defaultExpanded: autoExpandAll !== false, overrides: new Map() };
    }

    if (lastQueryRef.current !== queryKey) {
      lastQueryRef.current = queryKey;
      localVisibleRef.current = pageSize;
    }

    if (isLocal) {
      if (source.kind !== "local" && (refreshed || !localItemsRef.current)) {
        loadAllRemote(localItemsRef.current ? "reloading" : "initial");
        return;
      }

      computeLocal();
      dispatch({ type: "done" });

      return;
    }

    const previousRows = resultRef.current.rows.length;
    const preserveCount = refreshed && !paged ? previousRows : 0;

    reloadRemote(preserveCount, previousRows ? "reloading" : "initial");
  }, [
    autoExpandAll,
    computeLocal,
    expansionKey,
    isLocal,
    loadAllRemote,
    pageIndex,
    pageSize,
    paged,
    queryKey,
    refreshToken,
    reloadRemote,
    source,
  ]);

  useEffect(() => abort, [abort]);

  const loadMore = useCallback(() => {
    if (loadingRef.current) return;

    const { current } = paramsRef;
    const currentIsLocal = current.source.kind === "local" || !current.remote;

    if (currentIsLocal) {
      if (resultRef.current.hasMore) {
        localVisibleRef.current = resultRef.current.rows.length + current.pageSize;
        computeLocal();
        dispatch({ type: "touch" });
      }

      return;
    }

    const loader = loaderRef.current;

    if (!loader) return;

    const { frontier } = loader.flatten(expansionRef.current);

    if (!frontier) return;

    runLoad(async signal => {
      await loader.loadNode(frontier, expansionRef.current, signal);

      if (signal.aborted) return;

      commitRemote();
    }, "more");
  }, [commitRemote, computeLocal, runLoad]);

  const applyExpansion = useCallback(() => {
    const { current } = paramsRef;

    if (current.source.kind === "local" || !current.remote) computeLocal();
    else commitRemote();

    dispatch({ type: "touch" });
  }, [commitRemote, computeLocal]);

  const setGroupExpanded = useCallback(
    (path, expanded) => {
      expansionRef.current.overrides.set(pathKey(path), expanded);
      applyExpansion();
    },
    [applyExpansion]
  );

  const isGroupExpanded = useCallback(path => isExpandedIn(expansionRef.current, path), []);

  const setAllExpanded = useCallback(
    expanded => {
      expansionRef.current = { defaultExpanded: expanded, overrides: new Map() };
      applyExpansion();
    },
    [applyExpansion]
  );

  const getLoadedItems = useCallback(() => {
    const { root } = resultRef.current;

    if (!root) return [];

    return treeItems(root, paramsRef.current.groups.length);
  }, []);

  const retry = useCallback(() => {
    const { current } = paramsRef;

    if (current.source.kind === "local" || !current.remote) {
      if (current.source.kind !== "local") loadAllRemote("reloading");

      return;
    }

    if (loaderRef.current && resultRef.current.rows.length) {
      loadMore();
      return;
    }

    reloadRemote(0, "initial");
  }, [loadAllRemote, loadMore, reloadRemote]);

  const result = resultRef.current;

  return {
    rows: result.rows,
    totalCount: result.totalCount,
    groupCount: result.groupCount,
    hasMore: result.hasMore,
    pageCount: result.pageCount,
    grouped,
    phase: status.phase,
    error: status.error,
    version: status.version,
    loadMore,
    retry,
    setGroupExpanded,
    isGroupExpanded,
    setAllExpanded,
    getLoadedItems,
  };
}
